import { z } from 'zod/v4';

// ════════════════════════════════════════════════════════════════════════════
// `Hotels` — the first dedicated pilot format.
//
// GROUNDING (D28 — the population, enumerated and named).
//
// SOURCE OF RECORD, live: the 26 `hotels_search` projections captured in
//   flow-timelines/2026-08-08--kyoto-3-nights-near-gion-stay-finder-for-two
//     --bddb7afd/snapshot.json
// (`actionId: "searchapi.googleHotels"`, first projection at line 186521),
// carrying 252 property rows — 194 `hotel`, 58 `vacation_rental`. Cross-read
// against the 20 further rows of
//   flow-timelines/2026-08-08--kyoto-family-stay-near-gion-3-nights-late-
//     september--690c27d8/snapshot.json.
// The projection those rows are instances of is
// `SearchApiGoogleHotelsPropertyProjection`
// (apps/bl/src/business-logic/serpers/providers/searchapi/
//  searchapi-google-hotels.types.ts), itself re-typed 2026-08-12 against a
// separate 170-row capture set. A row record's payload IS that projection row
// plus the stay: `HotelsPropertyRowPayload = {property, stay}`.
//
// THE ONE THING THE CAPTURE CORRECTS ABOUT THE TYPE: the declared projection
// spells every soft field `?:` (absent), while the captured rows carry the KEY
// with an explicit `null`. So the schema below is `.nullable()`, not
// `.optional()` — written against what the wire holds, not against what the
// interface says it might.
//
// Counted presence over the 252 captured rows, which is what decides each
// field one at a time:
//
//   name, type, rating, reviews, image, link     252/252, never null
//   price_per_night_usd / total_stay_usd         null on 24 — SOLD OUT, and
//                                                the two are co-null. Absent
//                                                must never read as zero.
//   stars                                        null on exactly the 58
//                                                `vacation_rental` rows — a
//                                                type fact, not a data gap,
//                                                which is why `propertyType`
//                                                is carried beside it.
//   amenities                                    null on 10
//   location.city                                252/252, never null
//
// NOT CARRIED, and each for a stated reason rather than by omission:
//   essentials, best_offer   null on 252/252 here and 0 non-null in ANY
//                            captured run repo-wide. A field whose observed
//                            population is zero does not ship (D28); it
//                            arrives with the capture that first shows it.
//   property_token           a vendor continuation key. A persisted block is
//                            data, not a handle.
//   review_aspects, the four proximity ratings, gps, check-in/out times
//                            real and populated, and detail-view material —
//                            the payload is the TRIMMED render projection
//                            (§7.4), not the row.
//
// The row's `url` is its durable link — the property's own `link` (252/252),
// which is what a persisted card must point at. Hotels rows mint BOTH address
// halves (hotels-search.tool.ts `hotelsRowMints`, per URL-surface §3.3/R10):
// the vendor token the drill spends and the raw property URL the printed
// citation link binds through. An ACTION link can never land here: `link` is
// a vendor URL and the citation form is the only other source.
//
// The stay rides on the payload rather than inside a row, for the vendor's own
// reason: every price is a function of the window and party that produced it,
// so a price separated from its stay is a number with no meaning.
// ════════════════════════════════════════════════════════════════════════════

export const HOTELS_PAYLOAD_VERSION = 1;

/** The two property universes google_hotels answers in. Declared here rather
 *  than imported because the payload contract is shared and the vendor enum is
 *  BL's; the BL builder maps one to the other through an exhaustive `Record`,
 *  so a vendor-side member with no counterpart here fails to compile. */
export enum HotelsPropertyType {
  Hotel = 'hotel',
  VacationRental = 'vacation_rental',
}

/** The window and party the prices were quoted for. Mirrors the projection's
 *  echoed `stay` block, which the vendor returns rather than the tool
 *  assuming: an over-large party is folded before dispatch, so what was priced
 *  is not necessarily what was asked. */
export const hotelsStaySchema = z.object({
  checkInDate: z.string(),
  checkOutDate: z.string(),
  adults: z.number().int(),
  /** CSV of ages exactly as the vendor echoes it — the key is ABSENT on 6 of
   *  the 26 captured searches and present on 20, so null carries "no children
   *  in this search" rather than "unknown". */
  childrenAges: z.string().nullable(),
  currency: z.string(),
});

export const hotelsRowSchema = z.object({
  name: z.string(),
  propertyType: z.enum(HotelsPropertyType),
  url: z.string().nullable(),
  /** `price_per_night_usd`. Null on a sold-out row — never zero. */
  pricePerNightUsd: z.number().nullable(),
  /** `total_stay_usd` — the whole requested window, carried alongside the
   *  nightly figure and never multiplied out of it: the vendor rounds each
   *  independently and the two genuinely disagree. */
  totalStayUsd: z.number().nullable(),
  rating: z.number().nullable(),
  reviews: z.number().int().nullable(),
  /** `stars` (`extracted_hotel_class`). Null on every vacation rental. */
  stars: z.number().nullable(),
  amenities: z.array(z.string()).nullable(),
  city: z.string().nullable(),
  /** `image` — `images[0].original`, never the thumbnail half. */
  imageUrl: z.string().nullable(),
});

export const hotelsPayloadSchema = z.object({
  stay: hotelsStaySchema,
  rows: z.array(hotelsRowSchema).min(1),
});

export type HotelsStay = z.infer<typeof hotelsStaySchema>;
export type HotelsRow = z.infer<typeof hotelsRowSchema>;
export type HotelsPayload = z.infer<typeof hotelsPayloadSchema>;
